import dayjs from 'dayjs';
import { ReactElement, memo, useMemo } from 'react';
import { Event, EventProps, View, Views, ViewsProps } from 'react-big-calendar';
import BaseCalendar, { BaseCalendarProps } from '~/components/common/base-calendar/BaseCalendar';
import styles from './Calendar.module.scss';
import classNames from 'classnames/bind';
import { Box } from '@mui/material';
import { BaseScrollbar } from '~/components/common';
import React from 'react';
import { IScheduleEvent, IScheduleEventAssignee } from '~/utils/interface/schedule';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

type CalendarProps = Omit<BaseCalendarProps, 'view' | 'date' | 'onSelectEvent' | 'events'> & {
  view: View;
  date: string;
  events: Array<IScheduleEvent>;
  onSelectEvent: (event: IScheduleEvent) => void;
  resources?: Array<{ id: string | number; name: string; color?: string; [key: string]: any }>;
  resourceIdAccessor?: string;
  resourceTitleAccessor?: string;
};

const cx = classNames.bind(styles);

const views: ViewsProps = {
  month: true,
  week: true,
  day: true,
};

export const EVENT_COLOR_DEFAULT = '#FF3366';

const renderAssigneesNames = (assignees: Array<IScheduleEventAssignee> | null | undefined): string => {
  if (!assignees || assignees.length === 0) return '';
  const names = assignees.map(assignee => assignee.name).join(', ');
  return names;
};

const CollapseMonthView = memo((props: { event: IScheduleEvent }) => {
  const { event } = props;
  const eventColor = event.assigneeColor || EVENT_COLOR_DEFAULT;
  const timeRange = `${dayjs(event.startTime, 'HH:mm:ssZ').format('h A')} - ${dayjs(event.endTime, 'HH:mm:ssZ').format('h A')}`;
  const assigneesNames = renderAssigneesNames(event.assignees);

  return (
    <Box
      className={cx('event-container', 'event-container-month')}
      sx={{
        backgroundColor: eventColor,
        color: '#ffffff',
        fontSize: '10px',
        border: 'none',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Box className='text-ellipsis overflow-hidden'>{event.name} | {timeRange}</Box>
      {assigneesNames && (
        <Box className='text-ellipsis overflow-hidden mt-[2px]' sx={{ fontSize: '10px', opacity: 0.9 }}>
          {assigneesNames}
        </Box>
      )}
    </Box>
  );
});

const ExpandMonthView = memo((props: { event: IScheduleEvent }) => {
  const { event } = props;
  const eventColor = event.assigneeColor || EVENT_COLOR_DEFAULT;
  const timeRange = `${dayjs(event.startTime, 'HH:mm:ssZ').format('h A')} - ${dayjs(event.endTime, 'HH:mm:ssZ').format('h A')}`;
  const assigneesNames = renderAssigneesNames(event.assignees);

  return (
    <Box
      className={cx('event-container', 'event-container-month', 'event-container-detailed-type')}
      sx={{
        backgroundColor: eventColor,
        color: '#ffffff',
        fontSize: '10px',
        border: 'none',
      }}
    >
      <Box className='text-ellipsis overflow-hidden mb-[2px]'>{event.name} | {timeRange}</Box>
      {assigneesNames && (
        <Box className='text-ellipsis overflow-hidden' sx={{ fontSize: '10px', opacity: 0.9 }}>
          {assigneesNames}
        </Box>
      )}
    </Box>
  );
});

const ExtendWeekView = memo((props: { event: IScheduleEvent }) => {
  const { event } = props;
  const eventColor: string = event.assigneeColor || EVENT_COLOR_DEFAULT;
  const timeRange = `${dayjs(event.startTime, 'HH:mm:ssZ').format('h A')} - ${dayjs(event.endTime, 'HH:mm:ssZ').format('h A')}`;
  const assigneesNames = renderAssigneesNames(event.assignees);

  return (
    <Box
      className={cx(`event-container event-container-detailed-type flex flex-col`)}
      sx={{
        borderColor: eventColor,
        backgroundColor: eventColor,
        color: '#ffffff',
      }}
    >
      <Box className='truncate text-[12px] font-bold mb-[4px]'>{event.name}</Box>
      <Box className='truncate text-[10px] mb-[3px]'>{timeRange}</Box>
      {assigneesNames && (
        <Box className='truncate mt-[2px]' sx={{ fontSize: '12px', opacity: 0.9 }}>
          {assigneesNames}
        </Box>
      )}
    </Box>
  );
});

const CollapseWeekView = memo((props: { event: IScheduleEvent }) => {
  const { event } = props;
  const eventColor: string = event.assigneeColor || EVENT_COLOR_DEFAULT;
  const timeRange = `${dayjs(event.startTime, 'HH:mm:ssZ').format('h A')} - ${dayjs(event.endTime, 'HH:mm:ssZ').format('h A')}`;
  const assigneesNames = renderAssigneesNames(event.assignees);

  return (
    <Box
      className={cx(`event-container event-container-detailed-type flex flex-col`)}
      sx={{
        borderColor: eventColor,
        backgroundColor: eventColor,
        color: '#ffffff',
      }}
    >
      <Box className='truncate'>{event.name} | {timeRange}</Box>
      {assigneesNames && (
        <Box className='truncate mt-[2px]' sx={{ fontSize: '12px', opacity: 0.9 }}>
          {assigneesNames}
        </Box>
      )}
    </Box>
  );
});

const ExtendDayView = memo((props: { event: IScheduleEvent }) => {
  const { event } = props;
  const eventColor: string = event.assigneeColor || EVENT_COLOR_DEFAULT;
  const timeRange = `${dayjs(event.startTime, 'HH:mm:ssZ').format('h A')} - ${dayjs(event.endTime, 'HH:mm:ssZ').format('h A')}`;
  const assigneesNames = renderAssigneesNames(event.assignees);

  return (
    <Box
      className={cx(`event-container event-container-detailed-type flex flex-col`)}
      sx={{
        borderColor: eventColor,
        backgroundColor: eventColor,
        color: '#ffffff',
      }}
    >
      <Box className='truncate text-[12px] font-bold mb-[4px]'>{event.name}</Box>
      <Box className='truncate text-[10px] mb-[3px]'>{timeRange}</Box>
      {assigneesNames && (
        <Box className='truncate' sx={{ fontSize: '12px', opacity: 0.9 }}>
          {assigneesNames}
        </Box>
      )}
    </Box>
  );
});

const CollapseDayView = memo((props: { event: IScheduleEvent }) => {
  const { event } = props;
  const eventColor: string = event.assigneeColor || EVENT_COLOR_DEFAULT;
  const timeRange = `${dayjs(event.startTime, 'HH:mm:ssZ').format('h A')} - ${dayjs(event.endTime, 'HH:mm:ssZ').format('h A')}`;
  const assigneesNames = renderAssigneesNames(event.assignees);

  return (
    <Box
      className={cx(`event-container event-container-detailed-type flex flex-col`)}
      sx={{
        borderColor: eventColor,
        backgroundColor: eventColor,
        color: '#ffffff',
      }}
    >
      <Box className='truncate'>{event.name} | {timeRange}</Box>
      {assigneesNames && (
        <Box className='truncate mt-[2px]' sx={{ fontSize: '12px', opacity: 0.9 }}>
          {assigneesNames}
        </Box>
      )}
    </Box>
  );
});

const Calendar = (props: CalendarProps): ReactElement => {
  const { view, date, events, ref, onSelectEvent, resources, resourceIdAccessor, resourceTitleAccessor, ...restProps } = props;

  const prepareMonthEventsData = (events: Array<IScheduleEvent>): Array<Event> => {
    const monthEvents: Array<Event> = [];
    for (let event of events) {
      const eventDate = dayjs(event.startAt);
      let monthEvent = monthEvents.find((monthEvent: Event) => {
        return dayjs(monthEvent.start).isSame(eventDate, 'date');
      });

      if (!monthEvent) {
        monthEvent = {
          start: eventDate.toDate(),
          end: eventDate.endOf('date').toDate(),
          resource: [],
        };
        monthEvents.push(monthEvent);
      }

      monthEvent.resource.push(event);
    }
    return monthEvents;
  };

  const prepareDayEventsData = (events: Array<IScheduleEvent>): Array<Event> => {
    return events.map((event) => {
      const startTime = dayjs(event.startTime, 'HH:mm:ssZ');
      const endTime = dayjs(event.endTime, 'HH:mm:ssZ');
      const eventDate = dayjs(event.startAt);
      const startAt = eventDate.hour(startTime.hour()).minute(startTime.minute());
      const endAt = eventDate.hour(endTime.hour()).minute(endTime.minute());
      return {
        start: startAt.toDate(),
        end: endAt.toDate(),
        resource: [event],
      };
    });
  };

  const preparedEvents = useMemo(() => {
    if (props.view === Views.MONTH) {
      return prepareMonthEventsData(props.events);
    } else if (props.view === Views.DAY || props.view === Views.WEEK) {
      return prepareDayEventsData(props.events);
    }
  }, [props.view, props.events]);

  const currentHour = dayjs().hour();

  return (
    <Box className={cx('calendar-component')}>
      {view === Views.DAY && (
        <Box className={cx('day-header')}>
          <Box className={cx('day-header-time')}>Time</Box>
          <Box className={cx('day-header-schedule')}>Schedule</Box>
        </Box>
      )}
      <Box
        className={cx(
          `hour-${currentHour + 1}`,
          { 'h-[900px]': view === Views.MONTH },
          { 'calendar-day-view': view === Views.DAY },
          { 'calendar-week-view': view === Views.WEEK && !resources },
          { 'calendar-resource-view': view === Views.WEEK && resources }
        )}
      >
        {preparedEvents && (() => {
          const baseCalendarProps: any = {
            events: preparedEvents,
            selectable: true,
            toolbar: false,
            date,
            views,
            view,
            onSelectEvent: (event: any, e: any) => {
              e.preventDefault();
            },
            ...restProps,
          };

          if (resources) {
            baseCalendarProps.resources = resources;
            baseCalendarProps.resourceIdAccessor = resourceIdAccessor;
            baseCalendarProps.resourceTitleAccessor = resourceTitleAccessor;
          }

          baseCalendarProps.components = {
              event: ({ event }: EventProps) => {
                if (view === Views.MONTH) {
                  return (
                    <BaseScrollbar maxHeight='100px'>
                      <Box
                        className='flex flex-col gap-[4px] overflow-x-auto'
                        sx={{
                          scrollbarWidth: 'none', // Firefox
                          '&::-webkit-scrollbar': { display: 'none' }, // Chrome, Safari
                        }}
                      >
                        {event.resource.map((eventData: IScheduleEvent, index: number) => {
                          return (
                            <Box key={index} onClick={(e) => { e.stopPropagation(); onSelectEvent(eventData); }}>
                              {event.resource.length > 1 ? (
                                <CollapseMonthView event={eventData} />
                              ) : (
                                <ExpandMonthView event={eventData} />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </BaseScrollbar>
                  );
                } else if (view === Views.DAY) {
                  const eventData = event.resource[0] as IScheduleEvent;
                  const duration = dayjs(event.end).diff(dayjs(event.start), 'minutes');
                  return (
                    <Box className={cx(`flex size-full`)} onClick={(e) => { e.stopPropagation(); onSelectEvent(eventData); }}>
                      {duration > 60 ? <ExtendDayView event={eventData} /> : <CollapseDayView event={eventData} />}
                    </Box>
                  );
                } else if (view === Views.WEEK) {
                  const eventData = event.resource[0] as IScheduleEvent;
                  const duration = dayjs(event.end).diff(dayjs(event.start), 'minutes');
                  return (
                    <Box className={cx(`flex size-full`)} onClick={(e) => { e.stopPropagation(); onSelectEvent(eventData); }}>
                      {duration > 60 ? <ExtendWeekView event={eventData} /> : <CollapseWeekView event={eventData} />}
                    </Box>
                  );
                }
                return null;
              },
            };

          return <BaseCalendar {...baseCalendarProps} />;
        })()}
      </Box>
    </Box>
  );
};

export default memo(Calendar);
